import { z } from "zod";
import apiClient from "../../../shared/api/client";

const workspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string().nullable(),
  description: z.string().nullable(),
  logo: z.string().nullable(),
  cover: z.string().nullable(),
  isPublic: z.boolean(),
  current: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  userId: z.number().nullable(),
  userEmail: z.string().nullable(),
  userName: z.string().nullable(),
  bannedAt: z.string().nullable(),
  bannedReason: z.string().nullable(),
  bannedByAdminId: z.string().nullable(),
  bannedByAdminUsername: z.string().nullable(),
});

const workspacesResponseSchema = z.object({
  data: z.array(workspaceSchema),
});

const workspaceResponseSchema = z.object({
  data: workspaceSchema,
});

const successResponseSchema = z.object({
  success: z.boolean(),
});

const selectWorkspaceResponseSchema = z.object({
  error: z.boolean(),
  message: z.string().optional(),
  data: z.string().optional(),
});

type WorkspacePayload = {
  path: string;
  name: string;
  description?: string;
  logo?: string;
  cover?: string;
  isPublic?: boolean;
  selected?: boolean;
};

type UpdateWorkspacePayload = Partial<WorkspacePayload>;

export type Workspace = z.infer<typeof workspaceSchema>;

export const fetchWorkspaces = async (includeDeleted: boolean = false): Promise<Workspace[]> => {
  const queryParams = includeDeleted ? "?includeDeleted=true" : "";
  const response = await apiClient.get(`/admin/workspaces${queryParams}`, { timeout: 15000 });
  const parsed = workspacesResponseSchema.parse(response.data);
  return parsed.data;
};

export const fetchCurrentWorkspace = async (): Promise<Workspace | null> => {
  const response = await apiClient.get("/workspace");
  const parsed = z.object({ data: workspaceSchema.nullable() }).parse(response.data);
  return parsed.data ?? null;
};

export const selectWorkspacePath = async (): Promise<string | null> => {
  const response = await apiClient.get("/workspaces/select");
  const parsed = selectWorkspaceResponseSchema.parse(response.data);
  if (parsed.error) {
    return null;
  }
  return parsed.data ?? null;
};

export const createWorkspace = async (payload: WorkspacePayload): Promise<Workspace> => {
  const response = await apiClient.post("/workspaces", payload);
  const parsed = workspaceResponseSchema.parse(response.data);
  return parsed.data;
};

export const updateWorkspace = async (
  id: string,
  payload: UpdateWorkspacePayload
): Promise<Workspace> => {
  const response = await apiClient.put(`/workspaces/${id}`, payload);
  const parsed = workspaceResponseSchema.parse(response.data);
  return parsed.data;
};

export const deleteWorkspace = async (id: string): Promise<void> => {
  const response = await apiClient.delete(`/admin/workspaces/${id}`);
  successResponseSchema.parse(response.data);
};

export const useWorkspace = async (path: string): Promise<void> => {
  const response = await apiClient.post("/workspace/use", { path });
  successResponseSchema.parse(response.data);
};

export const banWorkspace = async (id: string, reason: string): Promise<void> => {
  const response = await apiClient.post(`/admin/workspaces/${id}/ban`, { reason });
  successResponseSchema.parse(response.data);
};

export const unbanWorkspace = async (id: string): Promise<void> => {
  const response = await apiClient.delete(`/admin/workspaces/${id}/ban`);
  successResponseSchema.parse(response.data);
};
